import {
  createRootRouteWithContext,
  Link,
  Outlet,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { BootstrapBanner } from "../components/BootstrapBanner";
import { ThemeMenu } from "../components/ThemeMenu";
import {
  useActiveLanguage,
  ActiveLanguageProvider,
} from "../lib/activeLanguage";
import {
  ensureCurrentUser,
  useCurrentUser,
  useLogout,
  type CurrentUser,
} from "../lib/currentUser";
import {
  AccentPaletteProvider,
  ACCENT_PALETTES,
  useAccentPalette,
} from "../lib/accentPalette";
import { FLAGS, FlagKey, setFlag, useFlags } from "../lib/featureFlags";
import { PAIR_COLORS, PairColorProvider, usePairColor } from "../lib/pairColor";
import { LANGUAGES, type LanguageCode, getLanguage } from "../lib/languages";
import { ThemeProvider } from "../lib/theme";

interface RouterContext {
  queryClient: QueryClient;
  // Populated by the root `beforeLoad`. Child routes that need to gate
  // on auth read this synchronously rather than awaiting another fetch.
  user: CurrentUser | null;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  // Run once per navigation. Primes the `current-user` query so any
  // descendant `useCurrentUser()` reads from the same cache, and
  // surfaces the resolved user into the router context for child
  // `beforeLoad` hooks to gate on without re-awaiting the network.
  beforeLoad: async ({ context }) => {
    const user = await ensureCurrentUser(context.queryClient);
    return { user };
  },
  component: RootComponent,
});

function RootComponent() {
  // The active-language provider wraps everything — both the
  // monastic practice shell and the normal shell — so any descendant
  // can call useActiveLanguage(). Defaulting to `de` means the
  // German experience is byte-for-byte unchanged until a UI surface
  // exposes the switcher (S3 in the multilingual plan).
  return (
    <ThemeProvider>
      <AccentPaletteProvider>
        <PairColorProvider>
          <ActiveLanguageProvider>
            <ShellSwitch />
          </ActiveLanguageProvider>
        </PairColorProvider>
      </AccentPaletteProvider>
    </ThemeProvider>
  );
}

function ShellSwitch() {
  const { pathname } = useLocation();
  // §12 — practice screen renders ONLY the exercise. No shell.
  const monastic = /^\/practice\/[^/]+/.test(pathname);
  // Public landing renders its own header + footer. The signed-in
  // SiteHeader has Practice/Skills/Lexicon/etc. links that 401 for
  // guests — we don't want them on the landing.
  const isLanding = pathname.replace(/\/$/, "") === "";

  if (isLanding) {
    return (
      <div className="min-h-screen bg-ink-50 text-ink-900">
        <Outlet />
      </div>
    );
  }

  if (monastic) {
    return (
      <div className="min-h-screen bg-ink-50 text-ink-900">
        <BootstrapBanner />
        {/* Exit affordance — the monastic shell strips the full nav,
            so a small back-to-home link is the only way out short
            of the browser back button. Top-left, ink-soft so it
            doesn't compete with the exercise itself. */}
        <div className="mx-auto max-w-2xl px-4 sm:px-6 pt-4">
          <Link
            to="/home"
            className="eyebrow inline-flex items-center gap-1 hover:text-ink-700"
          >
            ← exit practice
          </Link>
        </div>
        <Outlet />
      </div>
    );
  }

  return (
    <div className="instrument-bg min-h-screen flex flex-col bg-ink-50 text-ink-900">
      <BootstrapBanner />
      <SiteHeader />
      <main className="flex-1 settle-in">
        <Outlet />
      </main>
      <SiteFooter />
    </div>
  );
}

function SiteHeader() {
  // Layout: wordmark + Practice + UserMenu always inline. On sm:+
  // the secondary surfaces (Skills/Lexicon/Constructions/Browse/About,
  // LanguageSwitcher, FlagsMenu) sit inline too. On narrow viewports
  // they collapse behind a hamburger that opens a dropdown panel —
  // Diego (2026-05-23 feedback) called out that the previous
  // flex-wrap layout ate a second row of real estate at 390w.
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeMobile = () => setMobileOpen(false);
  const { data: user } = useCurrentUser();
  const signedIn = !!user;
  return (
    <header className="border-b border-ink-100 bg-paper relative">
      <nav className="mx-auto max-w-5xl px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-x-5 text-sm">
        <Link
          to="/"
          className="font-serif text-2xl text-accent tracking-tight hover:text-accent-hover transition-colors"
        >
          lingua
        </Link>

        {/* Dashboard link — only for signed-in users. The wordmark
            always points to the public landing (/), so this is how
            a signed-in learner gets back to their /home tile from
            anywhere in the app. */}
        {signedIn && (
          <Link
            to="/home"
            className="font-medium text-ink-900 hover:text-ink-700 transition-colors [&.active]:text-accent [&.active]:decoration-accent [&.active]:underline underline-offset-4 decoration-2"
          >
            Dashboard
          </Link>
        )}

        {/* Primary verb. preload="intent" starts /exercises/next on
            hover/touch so the (5-15s) scheduler roundtrip happens
            before the click instead of after. */}
        <Link
          to="/practice"
          preload="intent"
          className="font-medium text-ink-900 hover:text-ink-700 transition-colors [&.active]:text-accent [&.active]:decoration-accent [&.active]:underline underline-offset-4 decoration-2"
        >
          Practice
        </Link>

        {/* Reference surfaces — sm:+ only, hidden behind hamburger on
            mobile. */}
        <span
          aria-hidden
          className="hidden sm:inline-block h-4 w-px bg-ink-200"
        />
        <Link
          to="/skills"
          className="hidden sm:inline text-ink-500 hover:text-ink-900 transition-colors [&.active]:text-accent [&.active]:decoration-accent [&.active]:underline underline-offset-4 decoration-2"
        >
          Skills
        </Link>
        <Link
          to="/lexicon"
          className="hidden sm:inline text-ink-500 hover:text-ink-900 transition-colors [&.active]:text-accent [&.active]:decoration-accent [&.active]:underline underline-offset-4 decoration-2"
        >
          Lexicon
        </Link>
        <Link
          to="/constructions"
          className="hidden sm:inline text-ink-500 hover:text-ink-900 transition-colors [&.active]:text-accent [&.active]:decoration-accent [&.active]:underline underline-offset-4 decoration-2"
        >
          Constructions
        </Link>

        <span
          aria-hidden
          className="hidden sm:inline-block h-4 w-px bg-ink-200"
        />
        {signedIn && (
          <Link
            to="/progress"
            className="hidden sm:inline text-ink-500 hover:text-ink-900 transition-colors [&.active]:text-accent [&.active]:decoration-accent [&.active]:underline underline-offset-4 decoration-2"
          >
            Progress
          </Link>
        )}
        {signedIn && (
          <Link
            to="/history"
            className="hidden sm:inline text-ink-500 hover:text-ink-900 transition-colors [&.active]:text-accent [&.active]:decoration-accent [&.active]:underline underline-offset-4 decoration-2"
          >
            History
          </Link>
        )}
        <Link
          to="/browse"
          className="hidden sm:inline text-ink-400 hover:text-ink-700 transition-colors [&.active]:text-accent [&.active]:decoration-accent [&.active]:underline underline-offset-4 decoration-2"
        >
          Browse
        </Link>
        <Link
          to="/about"
          className="hidden sm:inline text-ink-400 hover:text-ink-700 transition-colors [&.active]:text-accent [&.active]:decoration-accent [&.active]:underline underline-offset-4 decoration-2"
        >
          About
        </Link>

        {/* Push the user/language/flags trio to the far right. UserMenu
            stays visible everywhere (identity at a glance); language +
            flags hide behind the hamburger on mobile. */}
        <span className="ml-auto" />
        <UserMenu />
        <span className="hidden sm:contents">
          <ThemeMenu />
          <LanguageSwitcher />
          <FlagsMenu />
        </span>

        {/* Hamburger — mobile only. Toggles the panel below. */}
        <button
          type="button"
          className="sm:hidden flex h-8 w-8 items-center justify-center rounded text-ink-700 hover:bg-ink-100"
          onClick={() => setMobileOpen((o) => !o)}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          aria-controls="mobile-nav"
        >
          <span aria-hidden className="text-base leading-none">
            {mobileOpen ? "✕" : "☰"}
          </span>
        </button>
      </nav>

      {/* Mobile dropdown panel. Clicking anywhere inside closes it so
          tapping a link navigates and shuts the menu in one gesture. */}
      {mobileOpen && (
        <div
          id="mobile-nav"
          className="sm:hidden border-t border-ink-100 bg-paper"
          onClick={closeMobile}
        >
          <div className="mx-auto max-w-5xl px-4 py-3 flex flex-col gap-2 text-sm">
            <Link
              to="/skills"
              className="text-ink-700 hover:text-ink-900 [&.active]:text-accent [&.active]:decoration-accent [&.active]:underline underline-offset-4 decoration-1"
            >
              Skills
            </Link>
            <Link
              to="/lexicon"
              className="text-ink-700 hover:text-ink-900 [&.active]:text-accent [&.active]:decoration-accent [&.active]:underline underline-offset-4 decoration-1"
            >
              Lexicon
            </Link>
            <Link
              to="/constructions"
              className="text-ink-700 hover:text-ink-900 [&.active]:text-accent [&.active]:decoration-accent [&.active]:underline underline-offset-4 decoration-1"
            >
              Constructions
            </Link>
            <Link
              to="/progress"
              className="text-ink-700 hover:text-ink-900 [&.active]:text-accent [&.active]:decoration-accent [&.active]:underline underline-offset-4 decoration-1"
            >
              Progress
            </Link>
            <Link
              to="/history"
              className="text-ink-700 hover:text-ink-900 [&.active]:text-accent [&.active]:decoration-accent [&.active]:underline underline-offset-4 decoration-1"
            >
              History
            </Link>
            <Link
              to="/browse"
              className="text-ink-500 hover:text-ink-900 [&.active]:text-accent [&.active]:decoration-accent [&.active]:underline underline-offset-4 decoration-1"
            >
              Browse
            </Link>
            <Link
              to="/about"
              className="text-ink-500 hover:text-ink-900 [&.active]:text-accent [&.active]:decoration-accent [&.active]:underline underline-offset-4 decoration-1"
            >
              About
            </Link>
            <div className="flex items-center gap-3 pt-2 border-t border-ink-100">
              <ThemeMenu />
              <LanguageSwitcher />
              <FlagsMenu />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function UserMenu() {
  const { data: user, isLoading } = useCurrentUser();
  const logoutM = useLogout();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (isLoading || !user) return null;

  const initial = (user.name || user.email || "?").charAt(0).toUpperCase();
  const signOut = async () => {
    setOpen(false);
    try {
      await logoutM.mutateAsync();
    } catch {
      /* surface nothing — UI just behaves like the user clicked sign in */
    }
    navigate({ to: "/login" });
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        title={user.name}
        className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-ink-100 text-ink-700 hover:bg-ink-200 transition-colors font-medium text-sm"
      >
        {user.pictureUrl ? (
          <img
            src={user.pictureUrl}
            alt=""
            className="h-8 w-8 rounded-full object-cover"
          />
        ) : (
          initial
        )}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 w-60 border border-ink-300 bg-paper z-20 text-sm"
        >
          <Link
            to="/me"
            onClick={() => setOpen(false)}
            className="block px-3 py-3 border-b border-ink-200 hover:bg-ink-100 transition-colors"
          >
            <div className="text-ink-900 font-medium truncate">{user.name}</div>
            <div className="text-ink-400 text-xs truncate">{user.email}</div>
            <div className="mt-1 inline-block text-[10px] uppercase tracking-wide text-ink-400 border border-ink-200 px-1.5 py-0.5 rounded">
              {user.role}
            </div>
          </Link>
          <Link
            to="/progress"
            onClick={() => setOpen(false)}
            className="block px-3 py-2 text-ink-700 hover:bg-ink-100 transition-colors"
          >
            Your progress
          </Link>
          <Link
            to="/history"
            onClick={() => setOpen(false)}
            className="block px-3 py-2 text-ink-700 hover:bg-ink-100 transition-colors"
          >
            Attempt history
          </Link>
          <Link
            to="/skills"
            onClick={() => setOpen(false)}
            className="block px-3 py-2 text-ink-700 hover:bg-ink-100 transition-colors"
          >
            Browse skills
          </Link>
          {user.role === "admin" && (
            <Link
              to="/admin/users"
              onClick={() => setOpen(false)}
              className="block px-3 py-2 text-ink-700 hover:bg-ink-100 transition-colors border-t border-ink-200"
            >
              Admin · Users
            </Link>
          )}
          {user.role === "admin" && (
            <Link
              to="/admin/reports"
              onClick={() => setOpen(false)}
              className="block px-3 py-2 text-ink-700 hover:bg-ink-100 transition-colors"
            >
              Admin · Reports
            </Link>
          )}
          <Link
            to="/login"
            onClick={() => setOpen(false)}
            className="block px-3 py-2 text-ink-700 hover:bg-ink-100 transition-colors border-t border-ink-200"
          >
            Switch user
          </Link>
          <button
            type="button"
            onClick={signOut}
            disabled={logoutM.isPending}
            className="w-full text-left px-3 py-2 text-ink-700 hover:bg-ink-100 transition-colors disabled:opacity-50"
          >
            {logoutM.isPending ? "Signing out…" : "Sign out"}
          </button>
        </div>
      )}
    </div>
  );
}

function LanguageSwitcher() {
  const { language, setLanguage } = useActiveLanguage();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Dismiss on outside click + Escape. Mirrors the MemoryStatsInfo
  // popover pattern. Hover-leave is unreliable on touch — click + key
  // is the durable pair.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const current = getLanguage(language);

  const pick = (code: LanguageCode) => {
    setOpen(false);
    if (code === language) return;
    // Wipe React Query state so the new language's queries can't
    // accidentally rehydrate from the previous language's cache.
    // Routes still use hard-coded "de" today (S4 wires them up),
    // so this is mostly forward-looking — but doing it now means
    // S4 has nothing to remember.
    queryClient.clear();
    setLanguage(code);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="inline-flex items-center gap-1 text-ink-700 hover:text-ink-900 eyebrow min-h-[2.75rem] min-w-[2.75rem] px-2"
        title={`Studying: ${current?.englishName ?? language}`}
      >
        <span className="font-mono">{language}</span>
        <span aria-hidden className="text-ink-300 text-[10px]">
          ▾
        </span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 w-64 border border-ink-300 bg-paper z-20"
        >
          <ul>
            {LANGUAGES.map((lang) => {
              const isActive = lang.code === language;
              return (
                <li key={lang.code}>
                  <button
                    role="menuitemradio"
                    aria-checked={isActive}
                    onClick={() => pick(lang.code)}
                    className={`w-full text-left px-3 py-2.5 flex items-baseline gap-2 hover:bg-ink-100 transition-colors ${
                      isActive ? "bg-ink-100" : ""
                    } ${lang.available ? "" : "opacity-70"}`}
                  >
                    <span className="font-mono text-ink-400 text-xs w-7 shrink-0">
                      {lang.glyph}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="text-ink-900 font-medium">
                        {lang.englishName}
                      </span>
                      <span className="text-ink-400 text-xs ml-2 font-serif">
                        {lang.nativeName}
                      </span>
                    </span>
                    {!lang.available && (
                      <span className="text-[10px] uppercase tracking-wide text-ink-400 shrink-0">
                        coming soon
                      </span>
                    )}
                    {isActive && (
                      <span aria-hidden className="text-ink-700">
                        ✓
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="border-t border-ink-200 px-3 py-2 text-[11px] text-ink-400">
            Progress is tracked separately per language.
          </div>
        </div>
      )}
    </div>
  );
}

function SiteFooter() {
  // The deadline countdown lives in the Home page's Deadline section
  // (and at the top of every page that needs it). Repeating it in
  // the footer made every page show "X days to B1" twice; the footer
  // now reads like a print colophon — wordmark + locale on the
  // left, a small Roman numeral year stamp on the right. The
  // numeral is the wink, not chrome.
  const { language } = useActiveLanguage();
  const year = new Date().getFullYear();
  return (
    <footer className="mt-16 sm:mt-24 border-t border-ink-100">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between eyebrow gap-3">
        <span className="flex items-center gap-2">
          <span className="font-serif normal-case tracking-normal text-ink-700 text-sm">
            lingua
          </span>
          <span aria-hidden className="text-ink-300">
            ·
          </span>
          <span>Phase 1</span>
          <span aria-hidden className="text-ink-300">
            ·
          </span>
          <span className="font-mono normal-case tracking-normal text-ink-500">
            {language}
          </span>
        </span>
        <span
          className="tabular-nums"
          title={`${year} · printed daily`}
          aria-label={String(year)}
        >
          {toRoman(year)}
        </span>
      </div>
    </footer>
  );
}

// Roman numeral year stamp for the footer colophon. Kept in this
// file (not lib/) because nothing else uses it; if a second caller
// appears, lift to lib/. 4-digit years only (handles 1..3999).
function toRoman(n: number): string {
  const pairs: readonly [number, string][] = [
    [1000, "M"],
    [900, "CM"],
    [500, "D"],
    [400, "CD"],
    [100, "C"],
    [90, "XC"],
    [50, "L"],
    [40, "XL"],
    [10, "X"],
    [9, "IX"],
    [5, "V"],
    [4, "IV"],
    [1, "I"],
  ];
  let out = "";
  let v = n;
  for (const [k, sym] of pairs) {
    while (v >= k) {
      out += sym;
      v -= k;
    }
  }
  return out;
}

function FlagsMenu() {
  const [open, setOpen] = useState(false);
  const flags = useFlags();
  const active = Object.entries(flags).filter(([, v]) => v).length;
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="text-ink-400 hover:text-ink-700 eyebrow"
        title="Toggle session feature flags"
        style={{ minWidth: "6ch" }}
      >
        flags{active > 0 ? ` (${active})` : ""}
      </button>
      {open && (
        <div
          className="absolute right-0 top-8 w-[min(20rem,90vw)] rounded border border-ink-200 bg-paper shadow-lg p-4 z-10"
          onMouseLeave={() => setOpen(false)}
        >
          <AccentPalettePicker />
          <PairColorPicker />
          <div className="eyebrow mb-3 mt-5">Session flags</div>
          <ul className="space-y-3">
            {(Object.keys(FLAGS) as FlagKey[]).map((key) => {
              const f = FLAGS[key];
              return (
                <li key={key} className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id={`flag-${key}`}
                    checked={flags[key]}
                    onChange={(e) => setFlag(key, e.target.checked)}
                    className="mt-1"
                  />
                  <label htmlFor={`flag-${key}`} className="flex-1 text-sm">
                    <div className="text-ink-900">{f.label}</div>
                    <div className="text-ink-400 text-xs">{f.description}</div>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

// Accent auditioning — swatch chips that flip the brand accent live so
// Jorge can compare hues in place before we commit to one. Lives in the
// flags panel since it's a temporary experiment surface, not shipped UI.
function AccentPalettePicker() {
  const { palette, setPalette } = useAccentPalette();
  return (
    <div>
      <div className="eyebrow mb-2">Accent palette</div>
      <div className="grid grid-cols-2 gap-2">
        {ACCENT_PALETTES.map((p) => {
          const isActive = p.value === palette;
          return (
            <button
              key={p.value}
              type="button"
              onClick={() => setPalette(p.value)}
              aria-pressed={isActive}
              className={`flex items-center gap-2 border px-2 py-1.5 text-left text-sm transition-colors ${
                isActive
                  ? "border-ink-900 bg-ink-100"
                  : "border-ink-200 hover:border-ink-400"
              }`}
              title={`Switch accent to ${p.label}`}
            >
              <span
                aria-hidden
                style={{ backgroundColor: p.swatch }}
                className="inline-block h-3.5 w-3.5 shrink-0 rounded-full"
              />
              <span className="text-ink-900 truncate">{p.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Pair auditioning — the SECONDARY hue that carries data accents (CEFR
// badges, headline numbers) alongside the primary. Same temporary
// experiment surface as the accent picker above.
function PairColorPicker() {
  const { pair, setPair } = usePairColor();
  return (
    <div className="mt-4">
      <div className="eyebrow mb-2">Pair color</div>
      <div className="grid grid-cols-2 gap-2">
        {PAIR_COLORS.map((p) => {
          const isActive = p.value === pair;
          return (
            <button
              key={p.value}
              type="button"
              onClick={() => setPair(p.value)}
              aria-pressed={isActive}
              className={`flex items-center gap-2 border px-2 py-1.5 text-left text-sm transition-colors ${
                isActive
                  ? "border-ink-900 bg-ink-100"
                  : "border-ink-200 hover:border-ink-400"
              }`}
              title={`Pair blue with ${p.label}`}
            >
              <span
                aria-hidden
                style={{ backgroundColor: p.swatch }}
                className="inline-block h-3.5 w-3.5 shrink-0 rounded-full"
              />
              <span className="text-ink-900 truncate">{p.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
